import React, { useRef, useState, useCallback } from "react";

import { PLAYBACK_API } from "../../utils/playbackUtils";

// 미착용 이벤트 상세.
//  - 감지 순간 '캡처(스냅샷)'는 즉시 표시(빠름).
//  - '앞뒤 30초 영상'은 버튼을 눌렀을 때만 on-demand 로드(로딩 지연 회피).
// 위반 종류(안전모/조끼/안전대/턱끈 등)와 무관하게 시각·카메라만 있으면 동작.

function pad2(n) {
  return String(n).padStart(2, "0");
}

// 이벤트 시각 → 그날 날짜(YYYY-MM-DD)와 하루 중 초(targetSec). 브라우저 로컬(KST) 기준.
function parseEventMoment(event) {
  const raw =
    event?.timestamp ||
    event?.createdAt ||
    event?.eventTime ||
    event?.displayTime ||
    event?.regDate ||
    "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const date = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const targetSec = d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
  return { date, targetSec, label: d.toLocaleString("ko-KR", { hour12: false }) };
}

export default function ViolationClipModal({ event, onClose }) {
  const videoRef = useRef(null);
  const retryRef = useRef(0); // <video> 재생 실패 시 자동 재시도 횟수
  const moment = parseEventMoment(event);

  // idle | loading | ready | error  (idle = 아직 영상 요청 안 함)
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");
  const [videoSrc, setVideoSrc] = useState("");
  const [seekOffset, setSeekOffset] = useState(0);

  // 오탐(AI 오인식) 신고 상태: idle | saving | done | error
  const [fp, setFp] = useState("idle");

  const reportFalsePositive = useCallback(async () => {
    const id = event?.id ?? event?.eventId ?? event?.idx;
    if (id == null) {
      setFp("error");
      return;
    }
    setFp("saving");
    try {
      const res = await fetch("/api/event/false-positive", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id, type: "PPE", value: true }),
      });
      const data = await res.json().catch(() => ({}));
      setFp(res.ok && data.success ? "done" : "error");
    } catch (_) {
      setFp("error");
    }
  }, [event]);

  // '앞뒤 30초 영상 보기' 클릭 시에만 실행 — 그전엔 영상 네트워크 요청 자체가 없음.
  //   서버 remux 경로(/api/event/:id/clip)를 사용 → faststart mp4라 브라우저 첫 시도 재생 가능.
  //   위반 직후엔 녹화 세그먼트가 아직 안 닫혀 준비 전일 수 있으므로, 준비될 때까지 자동 폴링(최대 25초).
  const loadVideo = useCallback(async () => {
    setStatus("loading");
    setErrMsg("");
    const id = event?.id ?? event?.eventId ?? event?.idx;
    if (id == null) {
      setErrMsg("이벤트 정보를 알 수 없습니다.");
      setStatus("error");
      return;
    }
    const started = Date.now();
    const MAX_WAIT_MS = 25000; // 위반 직후 세그먼트 닫힘까지 자동 대기
    const POLL_MS = 2500;

    const attempt = async () => {
      try {
        const res = await fetch(`/api/event/${id}/clip`, {
          credentials: "include",
          cache: "no-store",
        });
        const data = await res.json().catch(() => ({}));
        if (data && data.hasClip && data.videoUrl) {
          setVideoSrc(data.videoUrl); // 토큰 포함, faststart remux 클립
          setSeekOffset(Math.max(0, Number(data.seekOffsetSec || 0)));
          setStatus("ready");
          return;
        }
        // 아직 준비 전(녹화 진행 중/보관없음) → 자동 재시도
        if (Date.now() - started < MAX_WAIT_MS) {
          setTimeout(attempt, POLL_MS);
          return;
        }
        setErrMsg(data?.message || "영상을 준비 중입니다. 잠시 후 다시 시도해주세요.");
        setStatus("error");
      } catch (e) {
        if (Date.now() - started < MAX_WAIT_MS) {
          setTimeout(attempt, POLL_MS);
          return;
        }
        setErrMsg(e.message || "영상을 불러오지 못했습니다.");
        setStatus("error");
      }
    };
    attempt();
  }, [event]);

  // 영상 메타데이터 로드되면 위반 30초 전 지점으로 이동 후 재생
  const handleLoadedMetadata = () => {
    retryRef.current = 0; // 정상 로드 → 재시도 카운터 초기화
    const v = videoRef.current;
    if (!v) return;
    try {
      v.currentTime = seekOffset;
    } catch (_) {}
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  };

  // 재생 실패(막 닫힌 세그먼트 등) → 잠시 후 자동으로 다시 불러옴(최대 3회).
  const handleVideoError = () => {
    if (retryRef.current < 3) {
      retryRef.current += 1;
      setStatus("loading");
      setTimeout(() => loadVideo(), 2000);
    } else {
      setErrMsg("영상 재생에 실패했습니다. 잠시 후 다시 시도해주세요.");
      setStatus("error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-white p-4 shadow-2xl dark:bg-[#1F2A3B]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-base font-bold text-gray-900 dark:text-white">
              미착용 감지 캡처
            </div>
            <div className="truncate text-xs text-gray-500 dark:text-gray-400">
              {event?.deviceName || event?.deviceId || ""}
              {moment ? ` · ${moment.label}` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200"
          >
            닫기
          </button>
        </div>

        {/* 감지 순간 스냅샷 — 즉시 표시 */}
        {event?.imagePath ? (
          <div>
            <div className="mb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
              감지 순간 · 미착용 위치 표시
            </div>
            <img
              src={`${PLAYBACK_API}/api/playback/ppe-snapshot/${event.imagePath}`}
              alt="미착용 감지 스냅샷"
              className="max-h-[60vh] w-full rounded-xl bg-black object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        ) : (
          <div className="flex h-[240px] items-center justify-center px-6 text-center text-sm text-gray-400">
            이 이벤트에는 캡처 이미지가 없습니다.
          </div>
        )}

        {/* 앞뒤 30초 영상 — 버튼 클릭 시에만 로드(on-demand) */}
        <div className="mt-3">
          {status === "idle" && (
            <button
              type="button"
              onClick={loadVideo}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              ▶ 위반 전후 영상 보기
            </button>
          )}

          {status === "loading" && (
            <div className="flex h-[260px] items-center justify-center rounded-xl bg-black text-sm text-gray-300 sm:h-[360px]">
              영상 불러오는 중...
            </div>
          )}

          {status === "error" && (
            <div className="rounded-xl bg-black px-6 py-6 text-center text-sm text-gray-300">
              {errMsg}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={loadVideo}
                  className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-gray-200 hover:bg-white/20"
                >
                  다시 시도
                </button>
              </div>
            </div>
          )}

          {status === "ready" && (
            <div className="relative overflow-hidden rounded-xl bg-black">
              <video
                ref={videoRef}
                src={videoSrc}
                controls
                autoPlay
                playsInline
                onLoadedMetadata={handleLoadedMetadata}
                onError={handleVideoError}
                className="h-[260px] w-full bg-black object-contain sm:h-[360px]"
              />
            </div>
          )}
        </div>

        {/* AI 오탐(오인식) 신고 — 라벨링되어 재학습 데이터로 활용 */}
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-3 dark:border-white/10">
          <span className="text-[11px] text-gray-400">
            AI가 잘못 감지했나요? 신고하면 재학습에 반영됩니다.
          </span>
          {fp === "done" ? (
            <span className="shrink-0 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
              ✓ 오탐으로 신고됨
            </span>
          ) : (
            <button
              type="button"
              onClick={reportFalsePositive}
              disabled={fp === "saving"}
              className="shrink-0 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60 dark:bg-amber-500/15 dark:text-amber-400"
            >
              {fp === "saving" ? "신고 중..." : fp === "error" ? "다시 시도" : "이건 오탐이에요"}
            </button>
          )}
        </div>

        <div className="mt-2 text-[11px] leading-relaxed text-gray-400">
          ※ '영상 보기'를 누르면 위반 전후 구간이 재생됩니다. 녹화는 보관기간(기본 7일) 동안만 조회됩니다.
        </div>
      </div>
    </div>
  );
}
